import type { RootState } from '../index';
import { API_REDUCER_PATHS } from './apiRegistry';

/**
 * Collects RTK Query tags from queries that have active component subscriptions.
 * Returns tags in "type:id" format (e.g. "partner_campaigns:LIST", "partner_campaigns:409").
 * Only includes tags from queries that are currently watched by mounted React components.
 */
export const getActiveTags = (state: RootState): string[] => {
  const activeTags: Set<string> = new Set();

  for (const reducerPath of API_REDUCER_PATHS) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const apiState = (state as any)[reducerPath];
    if (!apiState) continue;

    const subscriptions = apiState.subscriptions;
    const providedKeys = apiState.provided?.keys;
    if (!subscriptions || !providedKeys) continue;

    // Collect query cache keys that have active component subscribers
    const activeQueryKeys = new Set<string>();
    for (const [queryKey, subs] of Object.entries(subscriptions)) {
      if (subs && Object.keys(subs as object).length > 0) {
        activeQueryKeys.add(queryKey);
      }
    }

    // Look up which tags those active queries provide
    for (const [queryKey, tags] of Object.entries(providedKeys)) {
      if (!activeQueryKeys.has(queryKey)) continue;
      if (!Array.isArray(tags)) continue;

      for (const tag of tags) {
        if (tag && typeof tag === 'object' && 'type' in tag) {
          const id = 'id' in tag ? String(tag.id) : 'LIST';
          activeTags.add(`${tag.type}:${id}`);
        }
      }
    }
  }

  return Array.from(activeTags);
};
